import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormShell, FormSection, Field, SaveBar, Notice } from '../patterns';
import { nutritionService, FertilizerProduct, FertilizerInput } from '../../services/nutritionService';
import { allowedUnitsForType } from '../../utils/units';
import './nutrition.css';

const NutritionProductForm: React.FC<{ product?: FertilizerProduct; onSave: () => void; onCancel: () => void }> = ({ product, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(product?.productName ?? '');
  const [n, setN] = useState(String(product?.analysis?.n ?? ''));
  const [p, setP] = useState(String(product?.analysis?.p ?? ''));
  const [k, setK] = useState(String(product?.analysis?.k ?? ''));
  const [methods, setMethods] = useState<string[]>(product?.applicationMethods ?? []);
  const [stockUnit, setStockUnit] = useState(product?.stockUnit ?? '');
  const [leadTimeDays, setLeadTimeDays] = useState(String(product?.leadTimeDays ?? ''));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const title = product ? t('nutrition.products.form.editTitle') : t('nutrition.products.form.newTitle');

  const toggleMethod = (m: string) =>
    setMethods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const body: FertilizerInput = {
        productName: name.trim(),
        analysis: { n: Number(n) || 0, p: Number(p) || 0, k: Number(k) || 0 },
        applicationMethods: methods as ('soil' | 'foliar')[],
        stockUnit,
        ...(leadTimeDays !== '' ? { leadTimeDays: Number(leadTimeDays) } : {}),
      };
      if (product) await nutritionService.updateFertilizer(product._id, body);
      else await nutritionService.createFertilizer(body);
      onSave();
    } catch {
      setSaveError(t('nutrition.products.form.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="nutrition-product-form">
      <FormShell
        sections={[{ id: 'np-prod', title }]}
        onSubmit={(e) => e?.preventDefault?.()}
        saveBar={
          <SaveBar
            status={saving ? { kind: 'saving' } : null}
            primaryAction={{ label: t('nutrition.products.form.save'), onClick: handleSave, disabled: saving || !name.trim() || !stockUnit }}
            secondaryActions={[{ label: t('nutrition.products.form.cancel'), tone: 'subtle', onClick: onCancel }]}
          />
        }
      >
        <FormSection id="np-prod" title={title}>
          {saveError && (
            <Notice tone="danger" className="mb-3">
              {saveError}
            </Notice>
          )}
          <div className="pat-field-grid pat-field-grid--2">
            <Field label={t('nutrition.products.form.name')} required controlId="np-name">
              <input id="np-name" className="pat-input" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={t('nutrition.products.form.stockUnit')} controlId="np-unit">
              <select id="np-unit" className="pat-input" value={stockUnit} onChange={(e) => setStockUnit(e.target.value)}>
                <option value="">{t('nutrition.products.form.stockUnitPlaceholder')}</option>
                {allowedUnitsForType('fertilizer').map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </Field>
            <Field label={t('nutrition.products.leadTimeDays')} controlId="np-lead-time">
              <input id="np-lead-time" type="number" min={0} className="pat-input" value={leadTimeDays} onChange={(e) => setLeadTimeDays(e.target.value)} />
            </Field>
          </div>
          <div className="pat-field-grid pat-field-grid--3">
            <Field label={t('nutrition.products.form.n')} controlId="np-n">
              <input id="np-n" type="number" className="pat-input" value={n} onChange={(e) => setN(e.target.value)} />
            </Field>
            <Field label={t('nutrition.products.form.p')} controlId="np-p">
              <input id="np-p" type="number" className="pat-input" value={p} onChange={(e) => setP(e.target.value)} />
            </Field>
            <Field label={t('nutrition.products.form.k')} controlId="np-k">
              <input id="np-k" type="number" className="pat-input" value={k} onChange={(e) => setK(e.target.value)} />
            </Field>
          </div>
          <Field label={t('nutrition.products.form.methods')} controlId="np-methods">
            <div className="ntr-chips" id="np-methods">
              {['soil', 'foliar'].map((m) => (
                <label key={m} className="ntr-chip">
                  <input type="checkbox" checked={methods.includes(m)} onChange={() => toggleMethod(m)} /> {t(`nutrition.products.form.${m}`)}
                </label>
              ))}
            </div>
          </Field>
        </FormSection>
      </FormShell>
    </div>
  );
};

export default NutritionProductForm;
